import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  orderBy,
  query,
  serverTimestamp,
  updateDoc,
  where,
  limit,
} from 'firebase/firestore';
import { db } from '@/lib/firebase';
import type { Supplier, SupplierPurchaseEntry } from '@/types/schemas';

function tsToMs(v: unknown): number | undefined {
  if (!v) return undefined;
  if (typeof v === 'number') return v;
  if (typeof v === 'object' && v !== null && 'toMillis' in v) {
    return (v as { toMillis: () => number }).toMillis();
  }
  return undefined;
}

function mapSupplier(id: string, data: Record<string, unknown>): Supplier {
  return {
    id,
    name: data.name as string,
    contactPhone: (data.contactPhone as string) ?? '',
    notes: (data.notes as string) ?? '',
    totalAmount: (data.totalAmount as number) ?? 0,
    advanceAmount: (data.advanceAmount as number) ?? 0,
    isActive: (data.isActive as boolean) ?? true,
    archivedAt: data.archivedAt ? tsToMs(data.archivedAt) : null,
    archivedBy: (data.archivedBy as string) ?? null,
    createdAt: tsToMs(data.createdAt),
    updatedAt: tsToMs(data.updatedAt),
  };
}

export async function fetchSuppliers(): Promise<Supplier[]> {
  const snap = await getDocs(query(collection(db, 'suppliers'), orderBy('name')));
  return snap.docs
    .map((d) => mapSupplier(d.id, d.data() as Record<string, unknown>))
    .filter((s) => s.isActive !== false);
}

export async function fetchArchivedSuppliers(): Promise<Supplier[]> {
  const snap = await getDocs(query(collection(db, 'suppliers'), orderBy('name')));
  return snap.docs
    .map((d) => mapSupplier(d.id, d.data() as Record<string, unknown>))
    .filter((s) => s.isActive === false);
}

export async function createSupplier(
  data: Omit<Supplier, 'id' | 'createdAt' | 'updatedAt' | 'archivedAt' | 'archivedBy'>,
): Promise<string> {
  const ref = await addDoc(collection(db, 'suppliers'), {
    ...data,
    isActive: true,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
  return ref.id;
}

export async function updateSupplier(id: string, data: Partial<Supplier>): Promise<void> {
  const rest = { ...data };
  delete rest.id;
  await updateDoc(doc(db, 'suppliers', id), {
    ...rest,
    updatedAt: serverTimestamp(),
  });
}

export async function archiveSupplier(id: string, uid: string): Promise<void> {
  await updateDoc(doc(db, 'suppliers', id), {
    isActive: false,
    archivedAt: serverTimestamp(),
    archivedBy: uid,
    updatedAt: serverTimestamp(),
  });
}

export async function restoreSupplier(id: string): Promise<void> {
  await updateDoc(doc(db, 'suppliers', id), {
    isActive: true,
    archivedAt: null,
    archivedBy: null,
    updatedAt: serverTimestamp(),
  });
}

export async function deleteSupplier(id: string): Promise<void> {
  const movements = await getDocs(
    query(
      collection(db, 'stock_movements'),
      where('supplierId', '==', id),
      limit(1),
    ),
  );
  if (!movements.empty) {
    throw new Error('Cannot delete supplier with stock movement history. Archive instead.');
  }
  await deleteDoc(doc(db, 'suppliers', id));
}

export async function fetchSupplierPurchases(supplierId: string): Promise<SupplierPurchaseEntry[]> {
  const snap = await getDocs(
    query(collection(db, 'supplier_purchases'), where('supplierId', '==', supplierId), limit(200)),
  );
  return snap.docs
    .map((d) => {
      const data = d.data();
      return {
        id: d.id,
        ...(data as Omit<SupplierPurchaseEntry, 'id'>),
        createdAt: tsToMs(data.createdAt),
      };
    })
    .sort((a, b) => (b.createdAt ?? 0) - (a.createdAt ?? 0));
}

export async function addSupplierPurchaseEntry(
  entry: Omit<SupplierPurchaseEntry, 'id' | 'createdAt'>,
  actorId: string,
): Promise<string> {
  const ref = await addDoc(collection(db, 'supplier_purchases'), {
    ...entry,
    actorId,
    createdAt: serverTimestamp(),
  });

  const supplierRef = doc(db, 'suppliers', entry.supplierId);
  const supplierSnap = await getDoc(supplierRef);
  if (supplierSnap.exists()) {
    const s = supplierSnap.data() as {
      totalAmount?: number;
      advanceAmount?: number;
    };
    await updateDoc(supplierRef, {
      totalAmount:
        entry.type === 'PURCHASE'
          ? (s.totalAmount ?? 0) + entry.amount
          : s.totalAmount ?? 0,
      advanceAmount:
        entry.type === 'ADVANCE'
          ? (s.advanceAmount ?? 0) + entry.amount
          : s.advanceAmount ?? 0,
      updatedAt: serverTimestamp(),
    });
  }

  return ref.id;
}
